import { Canvas, useThree } from '@react-three/fiber';
import { Suspense, useEffect } from 'react';
import { sceneTunnel } from './sceneTunnel';
import ThreeDimensionModelLoader from '../shared/ThreeDimensionModelLoader/Main';
import { useResponsive } from '../../hooks/useResponsive';

// Safety net: if the entire PersistentCanvas ever does unmount (e.g. app
// teardown / navigation away from the studio), release the WebGL context so
// iOS Safari can reclaim GPU resources immediately.
function ContextReleaser() {
  const { gl, invalidate } = useThree();
  useEffect(() => {
    const observer = new ResizeObserver(() => invalidate());
    observer.observe(gl.domElement);
    return () => {
      observer.disconnect();
      gl.dispose();
      gl.forceContextLoss();
    };
  }, [gl, invalidate]);
  return null;
}

export const PersistentCanvas = () => {
  const { isIOSDevice } = useResponsive();

  return (
    <Canvas
      shadows
      frameloop="demand"
      dpr={isIOSDevice ? Math.min(window.devicePixelRatio, 1.5) : 3}
      gl={{
        powerPreference: isIOSDevice ? 'low-power' : 'default',
        antialias: !isIOSDevice,
      }}
      // Initial camera. Each scene re-configures fov/near/far/position via
      // <CameraSetter /> when it mounts — see CameraSetter.tsx for why we
      // mutate the shared camera instead of using makeDefault.
      camera={{ near: 0.001, far: 100, fov: 45, position: [0, 0, 0.05] }}
    >
      <ContextReleaser />
      <Suspense fallback={<ThreeDimensionModelLoader />}>
        <sceneTunnel.Out />
      </Suspense>
    </Canvas>
  );
};

export default PersistentCanvas;
